import React, { useState, useEffect } from 'react'
import {
  UserIcon,
  UserPlusIcon,
  TrashIcon,
  ClockIcon,
  EnvelopeIcon,
  AtSymbolIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { TeamService } from '../../../../shared/services/teamService'
import ConfirmDialog from '../../../../shared/components/ConfirmDialog'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../../../shared/lib/format'

interface TeamMember {
  userId: {
    _id: string
    username: string
    email: string
    avatar?: string
  }
  role: 'owner' | 'editor' | 'viewer'
  status: 'active' | 'pending' | 'inactive'
  joinedAt: string
}

interface PendingInvitation {
  _id: string
  invitedEmail: string
  role: 'editor' | 'viewer'
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  createdAt: string
  expiresAt: string
  emailLogs?: any[]
  lastEmailStatus?: string
  token: string
}

interface TeamMembersListProps {
  teamId: string
  members: TeamMember[]
  userRole: 'owner' | 'editor' | 'viewer'
  onInviteMember: () => void
  onUpdateMembers: () => void
}

const TeamMembersList: React.FC<TeamMembersListProps> = ({
  teamId,
  members,
  userRole,
  onInviteMember,
  onUpdateMembers,
}) => {
  const { t } = useTranslation()
  const [removingMember, setRemovingMember] = useState<string | null>(null)
  const [pendingRemoveMemberId, setPendingRemoveMemberId] = useState<
    string | null
  >(null)
  const [pendingCancelInvitationId, setPendingCancelInvitationId] = useState<
    string | null
  >(null)
  const [pendingInvitations, setPendingInvitations] = useState<
    PendingInvitation[]
  >([])
  const [loadingInvitations, setLoadingInvitations] = useState(false)

  const canManageMembers = userRole === 'owner' || userRole === 'editor'

  // 컴포넌트 마운트 시 초대 목록 조회
  useEffect(() => {
    fetchPendingInvitations()
  }, [teamId])

  const fetchPendingInvitations = async () => {
    if (!canManageMembers) {
      return
    }

    setLoadingInvitations(true)
    try {
      const data = await TeamService.getInvitations(teamId)
      if (data.success) {
        const invitations = (data.invitations ??
          []) as unknown as PendingInvitation[]
        setPendingInvitations(
          invitations.filter(invitation => invitation.status === 'pending')
        )
      }
    } catch (error) {
      console.error('초대 목록 조회 실패:', error)
    } finally {
      setLoadingInvitations(false)
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    setRemovingMember(memberId)
    try {
      const data = await TeamService.removeTeamMember(teamId, memberId)
      if (data.success) {
        toast.success(t('team.removeSuccess'))
        onUpdateMembers()
      } else {
        toast.error(data.message || t('team.removeError'))
      }
    } catch (error) {
      console.error('팀원 제거 실패:', error)
      toast.error(t('team.networkError'))
    } finally {
      setRemovingMember(null)
    }
  }

  const handleChangeRole = async (
    memberId: string,
    newRole: 'editor' | 'viewer'
  ) => {
    try {
      const data = await TeamService.changeTeamMemberRole(
        teamId,
        memberId,
        newRole
      )
      if (data.success) {
        onUpdateMembers()
      } else {
        toast.error(data.message || t('team.roleChangeError'))
      }
    } catch (error) {
      console.error('역할 변경 실패:', error)
      toast.error(t('team.networkError'))
    }
  }

  const handleResendInvitation = async (invitationId: string) => {
    try {
      const data = await TeamService.resendInvitation(teamId, invitationId)
      if (data.success) {
        toast.success(t('team.resendSuccess'))
        fetchPendingInvitations()
      } else {
        toast.error(data.message || t('team.resendError'))
      }
    } catch (error) {
      console.error('이메일 재발송 실패:', error)
      toast.error(t('team.networkError'))
    }
  }

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const data = await TeamService.cancelInvitation(teamId, invitationId)
      if (data.success) {
        toast.success(t('team.cancelSuccess'))
        fetchPendingInvitations()
        onUpdateMembers()
      } else {
        toast.error(data.message || t('team.cancelError'))
      }
    } catch (error) {
      console.error('초대 취소 실패:', error)
      toast.error(t('team.networkError'))
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800'
      case 'editor':
        return 'bg-blue-100 text-blue-800'
      case 'viewer':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleText = (role: string) => {
    switch (role) {
      case 'owner':
        return t('team.roleOwner')
      case 'editor':
        return t('team.roleEditor')
      case 'viewer':
        return t('team.roleViewer')
      default:
        return role
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'inactive':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return t('team.active')
      case 'pending':
        return t('team.pending')
      case 'inactive':
        return t('team.inactive')
      default:
        return status
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* 헤더 */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <UserIcon className="mr-2 h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">
              {t('team.membersCount', { count: String(members.length) })}
              {pendingInvitations.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-600">
                  •{' '}
                  {t('team.pendingCount', {
                    count: String(pendingInvitations.length),
                  })}
                </span>
              )}
            </h3>
          </div>
          {canManageMembers && (
            <button
              onClick={onInviteMember}
              className="flex items-center rounded-lg px-3 py-1.5 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800"
            >
              <UserPlusIcon className="mr-1 h-4 w-4" />
              {t('team.inviteMember')}
            </button>
          )}
        </div>
      </div>

      {/* 멤버 목록 */}
      <div className="divide-y divide-gray-200">
        {members.map(member => (
          <div
            key={member.userId._id}
            className="px-6 py-4 transition-colors hover:bg-gray-50"
          >
            {/* 데스크톱 레이아웃 */}
            <div className="hidden items-center justify-between md:flex">
              <div className="flex min-w-0 flex-1 items-center">
                {/* 아바타 */}
                <div className="mr-4 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-600 font-medium text-white">
                  {member.userId.avatar ? (
                    <img
                      src={member.userId.avatar}
                      alt={member.userId.username}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-lg">
                      {member.userId.username.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                {/* 사용자 정보 */}
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center">
                    <h4 className="mr-3 text-base font-semibold text-gray-900">
                      {member.userId.username}
                    </h4>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRoleColor(member.role)} mr-2`}
                    >
                      {getRoleText(member.role)}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(member.status)}`}
                    >
                      {getStatusText(member.status)}
                    </span>
                  </div>

                  {/* 이메일 주소 - 더 강조된 표시 */}
                  <div className="mb-1 flex items-start">
                    <AtSymbolIcon className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                    <span
                      className="break-all text-sm font-medium text-gray-700"
                      title={member.userId.email}
                    >
                      {member.userId.email}
                    </span>
                  </div>

                  {/* 참여일 */}
                  <div className="flex items-center text-xs text-gray-500">
                    <ClockIcon className="mr-1.5 h-3 w-3 flex-shrink-0" />
                    <span>
                      {t('team.joinedAt', {
                        date: formatDate(member.joinedAt),
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* 액션 버튼들 */}
              {canManageMembers && member.role !== 'owner' && (
                <div className="ml-4 flex flex-shrink-0 items-center space-x-2">
                  {/* 역할 변경 */}
                  <div className="relative">
                    <select
                      value={member.role}
                      onChange={e =>
                        handleChangeRole(
                          member.userId._id,
                          e.target.value as 'editor' | 'viewer'
                        )
                      }
                      className="rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      disabled={userRole !== 'owner'}
                    >
                      <option value="editor">{t('team.roleEditor')}</option>
                      <option value="viewer">{t('team.roleViewer')}</option>
                    </select>
                  </div>

                  {/* 제거 버튼 */}
                  <button
                    onClick={() => setPendingRemoveMemberId(member.userId._id)}
                    disabled={removingMember === member.userId._id}
                    className="rounded-md border border-red-200 p-1.5 text-red-600 transition-colors hover:bg-red-50 hover:text-red-800 disabled:opacity-50"
                    title={t('team.removeMember')}
                  >
                    {removingMember === member.userId._id ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent"></div>
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* 모바일 레이아웃 */}
            <div className="md:hidden">
              <div className="flex items-start space-x-3">
                {/* 아바타 */}
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-600 font-medium text-white">
                  {member.userId.avatar ? (
                    <img
                      src={member.userId.avatar}
                      alt={member.userId.username}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-sm">
                      {member.userId.username.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  {/* 이름과 역할 */}
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {member.userId.username}
                    </h4>
                    {canManageMembers && member.role !== 'owner' && (
                      <button
                        onClick={() =>
                          setPendingRemoveMemberId(member.userId._id)
                        }
                        disabled={removingMember === member.userId._id}
                        className="ml-2 rounded p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-800 disabled:opacity-50"
                        title={t('team.removeMember')}
                      >
                        {removingMember === member.userId._id ? (
                          <div className="h-3 w-3 animate-spin rounded-full border border-red-600 border-t-transparent"></div>
                        ) : (
                          <TrashIcon className="h-3 w-3" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* 이메일 */}
                  <div className="mb-2 flex items-start">
                    <AtSymbolIcon className="mr-1.5 mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400" />
                    <span
                      className="break-all text-xs text-gray-700"
                      title={member.userId.email}
                    >
                      {member.userId.email}
                    </span>
                  </div>

                  {/* 하단 정보 */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getRoleColor(member.role)}`}
                      >
                        {getRoleText(member.role)}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(member.status)}`}
                      >
                        {getStatusText(member.status)}
                      </span>
                    </div>

                    {/* 역할 변경 (모바일) */}
                    {canManageMembers &&
                      member.role !== 'owner' &&
                      userRole === 'owner' && (
                        <select
                          value={member.role}
                          onChange={e =>
                            handleChangeRole(
                              member.userId._id,
                              e.target.value as 'editor' | 'viewer'
                            )
                          }
                          className="rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="editor">{t('team.roleEditor')}</option>
                          <option value="viewer">{t('team.roleViewer')}</option>
                        </select>
                      )}
                  </div>

                  {/* 참여일 */}
                  <div className="mt-1 flex items-center text-xs text-gray-500">
                    <ClockIcon className="mr-1 h-3 w-3 flex-shrink-0" />
                    <span>
                      {t('team.joinedAt', {
                        date: formatDate(member.joinedAt),
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 초대 대기 중인 멤버들 */}
      {canManageMembers &&
        (loadingInvitations || pendingInvitations.length > 0) && (
          <>
            <div className="border-y border-yellow-200 bg-yellow-50 px-6 py-3">
              <div className="flex items-center">
                <ClockIcon className="mr-2 h-4 w-4 text-yellow-600" />
                <h4 className="text-sm font-medium text-yellow-800">
                  {t('team.pendingMembers', {
                    count: String(pendingInvitations.length),
                  })}
                  {loadingInvitations && (
                    <span className="ml-2 text-xs text-yellow-600">
                      {t('team.loading')}
                    </span>
                  )}
                </h4>
              </div>
            </div>

            {loadingInvitations ? (
              <div className="px-6 py-8 text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-yellow-600"></div>
                <p className="mt-2 text-sm text-gray-500">
                  {t('team.loadingInvitations')}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {pendingInvitations.map(invitation => (
                  <div
                    key={invitation._id}
                    className="bg-yellow-50/30 px-6 py-4 transition-colors hover:bg-yellow-50"
                  >
                    {/* 데스크톱 레이아웃 */}
                    <div className="hidden items-center justify-between md:flex">
                      <div className="flex min-w-0 flex-1 items-center">
                        {/* 아바타 (초대 대기) */}
                        <div className="mr-4 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 font-medium text-white">
                          <ClockIcon className="h-6 w-6" />
                        </div>

                        {/* 초대 정보 */}
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center">
                            <h4 className="mr-3 text-base font-semibold text-gray-900">
                              {t('team.pendingFull')}
                            </h4>
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRoleColor(invitation.role)} mr-2`}
                            >
                              {getRoleText(invitation.role)}
                            </span>
                            <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                              {t('team.pending')}
                            </span>
                          </div>

                          {/* 초대된 이메일 주소 - 강조 표시 */}
                          <div className="mb-1 flex items-start">
                            <AtSymbolIcon className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600" />
                            <span
                              className="break-all text-sm font-semibold text-yellow-800"
                              title={invitation.invitedEmail}
                            >
                              {invitation.invitedEmail}
                            </span>
                          </div>

                          <div className="flex items-center space-x-4 text-xs text-gray-600">
                            <div className="flex items-center">
                              <ClockIcon className="mr-1.5 h-3 w-3 flex-shrink-0" />
                              <span>
                                {t('team.invitedAt', {
                                  date: formatDate(invitation.createdAt),
                                })}
                              </span>
                            </div>
                            {invitation.lastEmailStatus && (
                              <div
                                className={`flex items-center ${
                                  invitation.lastEmailStatus === 'sent'
                                    ? 'text-green-600'
                                    : invitation.lastEmailStatus === 'failed'
                                      ? 'text-red-600'
                                      : 'text-gray-500'
                                }`}
                              >
                                <EnvelopeIcon className="mr-1 h-3 w-3 flex-shrink-0" />
                                <span>
                                  {invitation.lastEmailStatus === 'sent'
                                    ? t('team.sentShort')
                                    : invitation.lastEmailStatus === 'failed'
                                      ? t('team.sendFailed')
                                      : '미발송'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 초대 관리 버튼들 */}
                      <div className="ml-4 flex flex-shrink-0 items-center space-x-2">
                        {/* 이메일 재발송 */}
                        <button
                          onClick={() => handleResendInvitation(invitation._id)}
                          className="rounded-md border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800"
                          title="초대 이메일 재발송"
                        >
                          {t('team.resend')}
                        </button>

                        {/* 초대 취소 */}
                        <button
                          onClick={() =>
                            setPendingCancelInvitationId(invitation._id)
                          }
                          className="rounded-md border border-red-200 p-1.5 text-red-600 transition-colors hover:bg-red-50 hover:text-red-800"
                          title="초대 취소"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* 모바일 레이아웃 */}
                    <div className="md:hidden">
                      <div className="flex items-start space-x-3">
                        {/* 아바타 */}
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 font-medium text-white">
                          <ClockIcon className="h-5 w-5" />
                        </div>

                        <div className="min-w-0 flex-1">
                          {/* 제목과 버튼 */}
                          <div className="mb-2 flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-900">
                              {t('team.pendingFull')}
                            </h4>
                            <button
                              onClick={() =>
                                setPendingCancelInvitationId(invitation._id)
                              }
                              className="ml-2 rounded p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-800"
                              title="초대 취소"
                            >
                              <TrashIcon className="h-3 w-3" />
                            </button>
                          </div>

                          {/* 이메일 */}
                          <div className="mb-2 flex items-start">
                            <AtSymbolIcon className="mr-1.5 mt-0.5 h-3 w-3 flex-shrink-0 text-yellow-600" />
                            <span
                              className="break-all text-xs font-semibold text-yellow-800"
                              title={invitation.invitedEmail}
                            >
                              {invitation.invitedEmail}
                            </span>
                          </div>

                          {/* 역할 및 상태 */}
                          <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getRoleColor(invitation.role)}`}
                              >
                                {getRoleText(invitation.role)}
                              </span>
                              <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                {t('team.pending')}
                              </span>
                            </div>

                            <button
                              onClick={() =>
                                handleResendInvitation(invitation._id)
                              }
                              className="rounded border border-blue-200 px-2 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800"
                              title="초대 이메일 재발송"
                            >
                              {t('team.resend')}
                            </button>
                          </div>

                          {/* 초대일 및 이메일 상태 */}
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <div className="flex items-center">
                              <ClockIcon className="mr-1 h-3 w-3 flex-shrink-0" />
                              <span>
                                {t('team.invitedAt', {
                                  date: formatDate(invitation.createdAt),
                                })}
                              </span>
                            </div>
                            {invitation.lastEmailStatus && (
                              <div
                                className={`flex items-center ${
                                  invitation.lastEmailStatus === 'sent'
                                    ? 'text-green-600'
                                    : invitation.lastEmailStatus === 'failed'
                                      ? 'text-red-600'
                                      : 'text-gray-500'
                                }`}
                              >
                                <EnvelopeIcon className="mr-1 h-3 w-3 flex-shrink-0" />
                                <span>
                                  {invitation.lastEmailStatus === 'sent'
                                    ? t('team.sentShort')
                                    : invitation.lastEmailStatus === 'failed'
                                      ? t('team.sendFailed')
                                      : '미발송'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

      {/* 빈 상태 */}
      {members.length === 0 &&
        pendingInvitations.length === 0 &&
        !loadingInvitations && (
          <div className="px-6 py-12 text-center">
            <UserIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <p className="mb-2 text-gray-500">아직 팀 멤버가 없습니다</p>
            {canManageMembers && (
              <button
                onClick={onInviteMember}
                className="font-medium text-blue-600 hover:text-blue-800"
              >
                {t('team.inviteFirst')}
              </button>
            )}
          </div>
        )}

      <ConfirmDialog
        isOpen={!!pendingRemoveMemberId}
        title={t('team.removeMemberTitle')}
        description={t('team.removeMemberDescription')}
        confirmLabel={t('team.removeMember')}
        isBusy={!!removingMember}
        onCancel={() => setPendingRemoveMemberId(null)}
        onConfirm={() => {
          const target = pendingRemoveMemberId
          setPendingRemoveMemberId(null)
          if (target) {
            handleRemoveMember(target)
          }
        }}
      />

      <ConfirmDialog
        isOpen={!!pendingCancelInvitationId}
        title={t('team.cancelInviteTitle')}
        description={t('team.cancelInviteDescription')}
        confirmLabel={t('team.cancelInvite')}
        onCancel={() => setPendingCancelInvitationId(null)}
        onConfirm={() => {
          const target = pendingCancelInvitationId
          setPendingCancelInvitationId(null)
          if (target) {
            handleCancelInvitation(target)
          }
        }}
      />
    </div>
  )
}

export default TeamMembersList
